import type {
  AuthObservabilityService,
  AuthService,
  ClientAppUserResult,
  ServiceSignInResult,
  ServiceSignUpResult,
  ServiceValidationResult,
  ServiceForgotPasswordResult,
  SignUpProps,
  SocialProfile,
  SocialProvider,
  TranslationPort,
  User,
  ValidateUserRequest,
} from '../../types';
import type { CognitoCode } from '../../types/cognito-code';
import { AUTH_LOCALE_KEYS } from '../../i18n-keys';
import { SignUpStatus } from '../../constants';
import { deserializeUser } from '../session-persistence';
import type { HttpClient } from '../http';
import { HttpResponseError } from '../http';
import { fetchClientAppUserStatus } from '../validate-user';
import { CLIENT_APP_FEATURES } from './features';

export type { HttpClient };

interface KeyTransforms {
  dasherize(obj: Record<string, unknown>): Record<string, unknown>;
  camelize(str: string): string;
}

interface LoginResponse {
  data: {
    id: string | number;
    attributes: Record<string, unknown>;
  };
}

interface RegisterResponse {
  data: Record<string, unknown>[];
  errors: Array<{ source?: { pointer?: string }; title?: string }>;
}

interface PasswordResponse {
  error?: string;
  success?: string;
}

interface SessionReader {
  loadUser(): User | null;
}

interface ClientAppAuthServiceDeps {
  http: HttpClient;
  keys: KeyTransforms;
  translation: TranslationPort;
  themeName: string;
  sessionReader: SessionReader;
  observability?: AuthObservabilityService;
}

export class ClientAppAuthService implements AuthService {
  private http: HttpClient;
  private keys: KeyTransforms;
  private t: TranslationPort['t'];
  private themeName: string;
  private sessionReader: SessionReader;
  private observability?: AuthObservabilityService;

  readonly supportedFeatures = CLIENT_APP_FEATURES;

  constructor(deps: ClientAppAuthServiceDeps) {
    this.http = deps.http;
    this.keys = deps.keys;
    this.t = deps.translation.t;
    this.themeName = deps.themeName;
    this.sessionReader = deps.sessionReader;
    this.observability = deps.observability;
  }

  async checkClientAppUser(email: string): Promise<ClientAppUserResult> {
    const result = await this.validateUser({ email });
    return { exists: result.isExisting, firstName: result.firstName, authMethods: result.authMethods };
  }

  async checkCognitoUser(_email: string): Promise<CognitoCode> {
    return 'AccountReady';
  }

  async signIn(email: string, password: string): Promise<ServiceSignInResult> {
    try {
      const response = await this.http.request<LoginResponse>(
        '/services/api/v4/auth/login', 'POST', { email, password }
      );
      if (response.data?.attributes) {
        const user: User = deserializeUser({ id: response.data.id, ...response.data.attributes });
        return { user };
      }
      throw new Error(this.t(AUTH_LOCALE_KEYS.INVALID_PASSWORD, { defaultValue: 'Invalid Email / Password combination.' }));
    } catch (error) {
      const err = error instanceof Error ? error
        : new Error(this.t(AUTH_LOCALE_KEYS.INVALID_PASSWORD, { defaultValue: 'Invalid Email / Password combination.' }));
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async signUp(props: SignUpProps): Promise<ServiceSignUpResult> {
    try {
      const response = await this.http.request<RegisterResponse>(
        '/services/api/v4/auth', 'POST', {
          data: {
            type: 'users',
            attributes: this.keys.dasherize(props as unknown as Record<string, unknown>),
          },
        }
      );

      if (response.errors?.length) {
        const messages = response.errors.map(({ source, title }) => {
          const attr = this.keys.camelize((source?.pointer || '').replace('/data/attributes/', ''));
          return `${attr}: ${title}`;
        });
        throw new Error(messages.join('; '));
      }

      const loginResult = await this.signIn(props.email, props.password);
      if (!loginResult.user) {
        // This should never happen, but we'll handle it gracefully.
        throw new Error(this.t(AUTH_LOCALE_KEYS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }));
      }
      return { status: SignUpStatus.Confirmed, user: loginResult.user };
    } catch (error) {
      const err = error instanceof Error ? error
        : new Error(this.t(AUTH_LOCALE_KEYS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }));
      this.observability?.dispatch({ type: 'register_failure', error: err });
      throw err;
    }
  }

  async confirmSignUp(_email: string, _code: string): Promise<ServiceSignUpResult> {
    return { status: SignUpStatus.Incomplete };
  }

  async resendSignUpCode(_email: string): Promise<void> {}

  async signOut(_redirectUrl?: string): Promise<void> {
    try {
      await this.http.request('/services/api/v3/users/logout', 'DELETE');
    } catch (err) {
      this.observability?.dispatch({ type: 'signout_failure', error: err });
    }
  }

  async validateUser(request: ValidateUserRequest): Promise<ServiceValidationResult> {
    try {
      return await fetchClientAppUserStatus(this.http, request);
    } catch (error) {
      const err = error instanceof Error ? error
        : new Error(this.t(AUTH_LOCALE_KEYS.VALIDATION_REQUEST_FAILED, { defaultValue: 'Request failed' }));
      this.observability?.dispatch({ type: 'email_lookup_failure', error: err });
      throw err;
    }
  }

  /** @deprecated Not supported on client-app — social sign-in uses SDK tokens via legacySocialSignIn. */
  async idpRedirectSignIn(_provider: SocialProvider, _customState?: string): Promise<void> {
    throw new Error('idpRedirectSignIn is not supported by ClientAppAuthService. Use legacySocialSignIn instead.');
  }

  async authenticateAfterRedirect(_provider: SocialProvider): Promise<ServiceSignInResult> {
    throw new Error('authenticateAfterRedirect is not supported in the client-app bridge. Use legacySocialSignIn instead.');
  }

  async legacySocialSignIn(profile: SocialProfile): Promise<ServiceSignInResult> {
    const loginPayload = {
      provider: profile.provider,
      email: profile.email,
      userId: profile.userId,
      accessToken: profile.accessToken,
      firstName: profile.firstName,
      lastName: profile.lastName,
      homePhone: profile.homePhone,
    };

    const attemptLogin = async (): Promise<{ user: User }> => {
      const response = await this.http.request<LoginResponse>(
        '/services/api/v4/auth/login', 'POST', loginPayload
      );
      if (response.data?.attributes) {
        return { user: deserializeUser({ id: response.data.id, ...response.data.attributes }) };
      }
      throw new Error('SSO authentication failed');
    };

    try {
      try {
        return await attemptLogin();
      } catch (loginErr) {
        const isUserNotFound = loginErr instanceof HttpResponseError &&
          loginErr.message.toLowerCase().includes('user cannot be found');
        if (!isUserNotFound) throw loginErr;

        await this.socialSignUp(profile);
        const result = await attemptLogin();
        if (!profile.homePhone) {
          return { user: result.user, needsPhone: true };
        }
        return result;
      }
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err, authMethod: profile.provider as 'google' | 'facebook' | 'apple' });
      throw err;
    }
  }

  private async socialSignUp(profile: SocialProfile): Promise<void> {
    const attributes = this.keys.dasherize({
      provider: profile.provider,
      email: profile.email,
      userId: profile.userId,
      accessToken: profile.accessToken,
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      privacyTerms: true,
      theme: this.themeName,
      ...(profile.homePhone ? { homePhone: profile.homePhone } : {}),
    });

    const response = await this.http.request<RegisterResponse>(
      '/services/api/v4/auth', 'POST', {
        data: { type: 'users', attributes },
      }
    );

    if (response.errors?.length) {
      const messages = response.errors.map(({ source, title }) => {
        const attr = this.keys.camelize((source?.pointer || '').replace('/data/attributes/', ''));
        return `${attr}: ${title}`;
      });
      throw new Error(messages.join('; '));
    }
  }

  async forgotPassword(email: string, theme: string): Promise<ServiceForgotPasswordResult> {
    try {
      const response = await this.http.request<PasswordResponse>(
        '/services/api/v3/users/password', 'POST', { email, theme }
      );
      if (response.error) {
        const errorMessage = this.t(AUTH_LOCALE_KEYS.INVALID_EMAIL, { defaultValue: 'Invalid Email.' });
        this.observability?.dispatch({ type: 'signin_failure', error: new Error(errorMessage) });
        return { error: errorMessage };
      }
      return { success: this.t(AUTH_LOCALE_KEYS.PASSWORD_RESET_EMAIL_SENT, { defaultValue: 'A message has been sent to reset your password. Please check your inbox.' }), nextStep: 'email_sent' };
    } catch (err) {
      const errorMessage = this.t(AUTH_LOCALE_KEYS.INVALID_EMAIL, { defaultValue: 'Invalid Email.' });
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      return { error: errorMessage };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ServiceForgotPasswordResult> {
    try {
      await this.http.request<PasswordResponse>(
        '/services/api/v3/users/password', 'PATCH', {
          'current-password': currentPassword,
          password: newPassword,
          'password-confirmation': newPassword,
          theme: this.themeName,
        }
      );
      return { success: this.t(AUTH_LOCALE_KEYS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' }) };
    } catch (err) {
      return { error: err instanceof Error ? err.message : this.t(AUTH_LOCALE_KEYS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }) };
    }
  }

  async confirmForgotPassword(_email: string, _code: string, password: string, theme: string): Promise<ServiceForgotPasswordResult> {
    try {
      const response = await this.http.request<PasswordResponse>(
        '/services/api/v3/users/password', 'PATCH', { password, passwordConfirmation: password, theme }
      );
      if (response.error) {
        const errorMessage = this.t(AUTH_LOCALE_KEYS.RESET_TOKEN_INVALID, { defaultValue: 'Reset Password Token is invalid.' });
        this.observability?.dispatch({ type: 'signin_failure', error: new Error(errorMessage) });
        return { error: errorMessage };
      }
      return { success: this.t(AUTH_LOCALE_KEYS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' }) };
    } catch (err) {
      const errorMessage = this.t(AUTH_LOCALE_KEYS.RESET_TOKEN_INVALID, { defaultValue: 'Reset Password Token is invalid.' });
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      return { error: errorMessage };
    }
  }

  async confirmUser(token: string, theme: string): Promise<ServiceSignInResult> {
    try {
      const response = await this.http.request<LoginResponse>(
        '/services/api/v3/users/confirmation', 'GET', { confirmationToken: token, theme }
      );
      const user: User = deserializeUser({ id: response.data.id, ...response.data.attributes });
      return { user };
    } catch {
      throw new Error(this.t(AUTH_LOCALE_KEYS.ACCOUNT_CONFIRMATION_FAILED, { defaultValue: 'Your account confirmation was not successful.' }));
    }
  }

  async clearStaleSession(): Promise<void> {
    // No-op for client-app auth — there's no IDP session to clear.
  }


  async deleteAccount(): Promise<void> {
    try {
      await this.http.request('/services/api/v4/user/delete', 'DELETE');
    } catch (err) {
      this.observability?.dispatch({ type: 'delete_account_failure', error: err });
      throw err;
    }
  }

  async submitPhone(userId: string | number, phone: string): Promise<void> {
    try {
      await this.http.request(
        `/services/api/v3/users/${userId}`, 'PATCH', {
          data: { attributes: { 'home-phone': phone }},
        }
      );
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async refreshSession(): Promise<ServiceSignInResult | null> {
    const user = this.sessionReader.loadUser();
    return user ? { user } : null;
  }

  async getSignInProvider(): Promise<string | null> {
    const user = this.sessionReader.loadUser();
    const provider = user?.ssoProvider ?? null;
    // The backend echoes the Cognito sign-in method in sso-provider, so password
    // sessions arrive as 'password'. That's a sign-in method, not a federated SSO
    // identity — reporting it as a provider would hide email/password editing in
    // the profile for regular users. Only real SSO providers count.
    return provider === 'password' ? null : provider;
  }

  getLogoutUrl(_redirectUri: string): string | null {
    return null;
  }

  async updateProfile(params: User): Promise<{ success: true } | { error: string }> {
    try {
      await this.http.request(
        `/services/api/v3/users/${params.id}`, 'PATCH', {
          data: { attributes: this.keys.dasherize(params as unknown as Record<string, unknown>) },
        }
      );
      return { success: true };
    } catch (err) {
      return { error: err instanceof Error ? err.message : this.t(AUTH_LOCALE_KEYS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }) };
    }
  }

  async updateEmail(currentUser: User, newEmail: string, retypeEmail: string, password?: string, passwordConfirmation?: string): Promise<{ success: true } | { error: string }> {
    try {
      const attributes: Record<string, string> = {
        'current-email': currentUser.email,
        email: newEmail,
        'email-confirmation': retypeEmail,
      };
      if (password) attributes['password'] = password;
      if (passwordConfirmation) attributes['password-confirmation'] = passwordConfirmation;

      await this.http.request(
        '/services/api/v3/users/email', 'PATCH', {
          data: { attributes },
        }
      );
      return { success: true };
    } catch (err) {
      return { error: err instanceof Error ? err.message : this.t(AUTH_LOCALE_KEYS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }) };
    }
  }

  async confirmEmailUpdate(_code: string): Promise<{ success: true } | { error: string }> {
    return { error: 'Email confirmation via code is not supported. Please check your email for a confirmation link.' };
  }
}
